import type { ProgramDayExercise } from "@/api/db/schema/program-days";
import {
  type UploadSuccessInfo,
  useUploadStore,
} from "@/lib/upload/upload-store";
import { useDebouncedCallback } from "@/lib/use-debounced-callback";
import { cn } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";
import {
  type ColumnDef,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Check,
  ChevronRight,
  History,
  Info,
  Loader2,
  MessageSquare,
  Play,
  RefreshCw,
  Timer,
  Trash2,
  Video as VideoIcon,
} from "lucide-react";
import {
  type KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader } from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Textarea } from "../ui/textarea";
import { ExerciseDetailDialog } from "./ExerciseDetailDialog";
import { RPEBadge, RPEInput } from "./RPEInput";
import { VideoTrimmerDialog } from "./VideoTrimmerDialog";
import { WeightInput } from "./WeightInput";
import { WorkoutHistoryDrawer } from "./WorkoutHistoryDrawer";

// Extended exercise type with calculated values
export interface TrainingExercise extends ProgramDayExercise {
  calculatedTargetWeight: number | null;
  isTargetWeightEstimated?: boolean;
}

export enum MuscleGroup {
  CHEST = 0,
  BACK = 1,
  SHOULDERS = 2,
  BICEPS = 3,
  TRICEPS = 4,
  LEGS = 5,
  GLUTES = 6,
  CORE = 7,
  FULL_BODY = 8,
  OTHER = 9,
}
// Row data for the table
export interface ExerciseRowData {
  exercise: TrainingExercise;
  setLogs: SetLogData[];
  exerciseInfo?: {
    videoUrl?: string | null;
    descriptionUrl?: string | null;
    instructions?: string | null;
    muscleGroup?: MuscleGroup | string | null;
  };
}

export interface SetLogData {
  logId?: string | null;
  setNumber: number;
  targetWeight: number | null;
  targetReps: string;
  targetRPE: number | null;
  completedWeight: number | null;
  completedReps: number | null;
  completedRPE: number | null;
  notes?: string | null;
  videoUrl?: string | null;
  videoThumbnailUrl?: string | null;
  videoTrimStart?: number | null;
  videoTrimEnd?: number | null;
  videoProcessingStatus?: "pending" | "processing" | "done" | "error" | null;
}

interface SmartTrainingTableProps {
  exercises: ExerciseRowData[];
  onLogUpdate: (
    exerciseId: string,
    exerciseOrder: number,
    setNumber: number,
    data: Partial<SetLogData>,
  ) => void;
  onLogUpdateAsync?: (
    exerciseId: string,
    exerciseOrder: number,
    setNumber: number,
    data: Partial<SetLogData>,
  ) => Promise<string | null | undefined>;
  isLoading?: boolean;
  className?: string;
  disabled?: boolean;
  userRole?: string | null;
  isCoachingClient?: boolean;
  weekNumber?: number | null;
}

// Internal type for flattened table data
interface FlattenedRow {
  id: string;
  type: "exercise" | "set";
  exerciseData: ExerciseRowData;
  setData: SetLogData | null;
  isFirstSet: boolean;
}

interface NoteInputProps {
  initialValue: string;
  onSave: (value: string) => void;
  disabled?: boolean;
}

interface RepsInputProps {
  value: number | null;
  placeholder?: string;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

const RepsInput = memo(function RepsInput({
  value,
  placeholder,
  onChange,
  disabled,
  className,
  "data-testid": dataTestId,
}: RepsInputProps) {
  const [localValue, setLocalValue] = useState<string>(value?.toString() ?? "");
  const [isFocused, setIsFocused] = useState(false);
  // Ref always holds the latest typed value — avoids stale closure in handleBlur
  const latestValueRef = useRef(localValue);

  // Track the last value sent to onChange to prevent double-firing mutations
  const lastSavedValueRef = useRef<number | null>(value ?? null);

  // Sync when external value changes (e.g. day switch).
  // lastSavedValueRef is NOT updated on isFocused change to avoid overwriting
  // the ref between blur and the pending debounce timer.
  // biome-ignore lint/correctness/useExhaustiveDependencies: isFocused intentionally omitted — see comment above
  useEffect(() => {
    if (!isFocused) {
      const str = value?.toString() ?? "";
      setLocalValue(str);
      latestValueRef.current = str;
      lastSavedValueRef.current = value ?? null;
    }
  }, [value]);

  const debouncedSave = useDebouncedCallback(
    (raw: string) => {
      const num = raw === "" ? null : Number.parseFloat(raw);
      if (num !== lastSavedValueRef.current) {
        lastSavedValueRef.current = num;
        onChange(num);
      }
    },
    { wait: 600 },
  );

  const handleBlur = () => {
    setIsFocused(false);
    // Use ref — always the latest value regardless of React batching
    const num =
      latestValueRef.current === ""
        ? null
        : Number.parseFloat(latestValueRef.current);
    if (num !== lastSavedValueRef.current) {
      lastSavedValueRef.current = num;
      onChange(num);
    }
  };

  return (
    <Input
      type="number"
      inputMode="numeric"
      value={localValue}
      placeholder={placeholder}
      data-testid={dataTestId}
      onChange={(e) => {
        latestValueRef.current = e.target.value; // sync ref first
        setLocalValue(e.target.value);
        debouncedSave(e.target.value);
      }}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      disabled={disabled}
      className={className}
    />
  );
});

const NoteInput = memo(function NoteInput({
  initialValue,
  onSave,
  disabled,
}: NoteInputProps) {
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const valueRef = useRef(value);

  // Sync when external value changes (e.g. day switch).
  // Don't overwrite while focused — user is actively editing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: isFocused intentionally omitted — see comment above
  useEffect(() => {
    if (!isFocused) {
      setValue(initialValue);
      valueRef.current = initialValue;
    }
  }, [initialValue]);

  const handleSave = () => {
    if (valueRef.current !== initialValue) {
      onSave(valueRef.current);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
      (e.target as HTMLElement).blur();
    }
  };

  return (
    <div className="group flex w-full items-center justify-center gap-2">
      <Textarea
        placeholder="Not ekle..."
        className="h-auto min-h-[20px] w-full resize-none border-none bg-transparent py-1 text-center text-xs placeholder:text-center placeholder:text-muted-foreground/50 focus-visible:ring-0"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          valueRef.current = e.target.value;
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          handleSave();
        }}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {(value !== initialValue || isFocused) && !disabled && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-6 w-6 shrink-0 text-primary hover:bg-primary/10"
          onClick={handleSave}
          onMouseDown={(e) => e.preventDefault()} // Prevent blur before click
        >
          <Check className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
});

export function SmartTrainingTable({
  exercises,
  onLogUpdate,
  onLogUpdateAsync,
  isLoading = false,
  className,
  disabled = false,
  userRole,
  isCoachingClient = false,
  weekNumber,
}: SmartTrainingTableProps) {
  const uploadingItems = useUploadStore((s) => s.items);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Ref to always access latest exercises without triggering re-renders
  const exercisesRef = useRef(exercises);
  exercisesRef.current = exercises;
  const [selectedExercise, setSelectedExercise] =
    useState<ExerciseRowData | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [videoTrimmerOpen, setVideoTrimmerOpen] = useState(false);
  const [viewingVideoSet, setViewingVideoSet] = useState<{
    logId?: string | null;
    exerciseId: string;
    exerciseOrder: number;
    setNumber: number;
    videoUrl: string;
    thumbnailUrl?: string | null;
    reps?: number | string | null;
    weekNumber?: number | null;
  } | null>(null);
  const [activeSetForVideo, setActiveSetForVideo] = useState<{
    logId?: string | null;
    exerciseId: string;
    exerciseOrder: number;
    setNumber: number;
    reps?: number | string | null;
    existingVideoUrl?: string | null;
    existingThumbnailUrl?: string | null;
  } | null>(null);
  const [historyExercise, setHistoryExercise] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Video silme mutation — S3 + object tablosunu temizler
  const deleteVideoMutation = useMutation({
    mutationFn: async (params: {
      videoKey: string;
      thumbnailKey?: string;
    }) => {
      const { deleteVideoFn } = await import("@/actions/workout/server");
      return deleteVideoFn({ data: params });
    },
    onError: (err) => {
      console.error("[deleteVideoMutation] S3 video silinemedi:", err);
    },
  });

  // SQS trim job tetikleme mutation
  const triggerTrimMutation = useMutation({
    mutationFn: async (params: {
      logId: string;
      sourceKey: string;
      trimStart: number;
      trimEnd: number;
      duration: number;
    }) => {
      const { triggerVideoTrimFn } = await import("@/actions/workout/server");
      return triggerVideoTrimFn({ data: params });
    },
    onError: (err) => {
      console.error("[triggerTrimMutation] SQS tetikleme hatası:", err);
    },
  });

  // Flatten data for table: each set becomes a row
  const tableData = useMemo(() => {
    const rows: FlattenedRow[] = [];

    for (const exerciseData of exercises) {
      // Parent row (exercise) - include order for duplicate exercises
      rows.push({
        id: `${exerciseData.exercise.exerciseId}-${exerciseData.exercise.order}`,
        type: "exercise",
        exerciseData,
        setData: null,
        isFirstSet: true,
      });

      // Child rows (sets) - only if more than 1 set
      if (exerciseData.setLogs.length > 1) {
        for (let i = 0; i < exerciseData.setLogs.length; i++) {
          rows.push({
            id: `${exerciseData.exercise.exerciseId}-${exerciseData.exercise.order}-set-${i + 1}`,
            type: "set",
            exerciseData,
            setData: exerciseData.setLogs[i],
            isFirstSet: i === 0,
          });
        }
      }
    }

    return rows;
  }, [exercises]);

  // Extract a default numeric value from reps string (e.g. "8-10" -> "8", "5.5" -> "5.5")
  const extractDefaultReps = useCallback((repsStr: string) => {
    const match = repsStr.match(/\d+(\.\d+)?/);
    return match ? match[0] : "";
  }, []);

  // Handle weight/RPE/notes updates
  // Uses ref for exercises to avoid re-creating on every data change
  const handleUpdate = useCallback(
    (
      exerciseId: string,
      exerciseOrder: number,
      setNumber: number,
      data: Partial<SetLogData>,
    ) => {
      onLogUpdate(exerciseId, exerciseOrder, setNumber, data);
    },
    [onLogUpdate],
  );

  // Ref to always access latest handleUpdate without triggering column re-renders
  const handleUpdateRef = useRef(handleUpdate);
  handleUpdateRef.current = handleUpdate;

  const handleVideoUpload = useCallback(
    async (info: UploadSuccessInfo) => {
      const {
        publicUrl,
        trimStart,
        trimEnd,
        duration,
        exerciseId,
        exerciseOrder,
        setNumber,
      } = info;

      if (!exerciseId || exerciseOrder === undefined || !setNumber) {
        console.error(
          "Missing exercise or set info in upload success callback",
        );
        return;
      }

      // Find the specific setLog from exercises prop
      const exerciseData = exercises.find(
        (e) =>
          e.exercise.exerciseId === exerciseId &&
          e.exercise.order === exerciseOrder,
      );
      const setLog = exerciseData?.setLogs.find(
        (s) => s.setNumber === setNumber,
      );

      // Eğer bu set için daha önce yüklenmiş bir video varsa, önce eski S3
      // dosyasını ve object kaydını temizle — orphan oluşmasını engeller
      if (setLog?.videoUrl) {
        await deleteVideoMutation.mutateAsync({
          videoKey: setLog.videoUrl,
          thumbnailKey: setLog.videoThumbnailUrl ?? undefined,
        });
      }

      // DB'ye videoUrl + processingStatus yaz (async — logId döner)
      const savedLogId = onLogUpdateAsync
        ? await onLogUpdateAsync(exerciseId, exerciseOrder, setNumber, {
            videoUrl: publicUrl,
            videoProcessingStatus: "pending",
            videoTrimStart: trimStart,
            videoTrimEnd: trimEnd,
          })
        : null;

      // SQS'e mesaj gönder
      if (savedLogId) {
        const sourceKey = publicUrl.split("/").slice(-2).join("/");
        await triggerTrimMutation.mutateAsync({
          logId: savedLogId,
          sourceKey,
          trimStart,
          trimEnd,
          duration,
        });
      }
    },
    [onLogUpdateAsync, deleteVideoMutation, triggerTrimMutation, exercises],
  );

  // Open exercise detail
  const handleExerciseClick = useCallback((exerciseData: ExerciseRowData) => {
    setSelectedExercise(exerciseData);
    setIsDetailOpen(true);
  }, []);

  // Format rest seconds
  const formatRest = useCallback((seconds: number | null) => {
    if (!seconds) return "-";
    if (seconds < 60) return `${seconds}sn`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs === 0
      ? `${mins}dk`
      : `${mins}:${secs.toString().padStart(2, "0")}dk`;
  }, []);

  // Column definitions
  const columns = useMemo<ColumnDef<FlattenedRow>[]>(() => {
    return [
      {
        id: "exercise",
        header: () => <div className="min-w-[200px]">Hareket</div>,
        cell: ({ row }: { row: { original: FlattenedRow } }) => {
          const data = row.original;

          if (data.type === "set") {
            return (
              <div className="flex items-center">
                <div className="flex flex-col items-center">
                  <span className="w-8 shrink-0 text-center font-mono text-muted-foreground text-sm opacity-50">
                    {data.setData?.setNumber}
                  </span>
                </div>
                <div className="h-px w-4 bg-border" />
              </div>
            );
          }

          const exercise = data.exerciseData.exercise;
          const hasMultipleSets = data.exerciseData.setLogs.length > 1;

          return (
            <div className="flex min-w-0 flex-col gap-1 sm:min-w-[200px]">
              <div className="flex min-w-0 items-center gap-2">
                {hasMultipleSets && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    data-testid={`expand-exercise-${data.exerciseData.exercise.exerciseId}-${data.exerciseData.exercise.order}`}
                    onClick={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [data.id]: !prev[data.id],
                      }))
                    }
                  >
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 transition-transform duration-200",
                        expanded[data.id] && "rotate-90",
                      )}
                    />
                  </Button>
                )}
                <button
                  type="button"
                  className="group flex items-center gap-1 text-left font-medium text-primary hover:underline md:max-w-36"
                  onClick={() => handleExerciseClick(data.exerciseData)}
                >
                  <span className="overflow-hidden text-ellipsis">
                    {exercise.exerciseName || "Hareket"}
                  </span>
                  <Info className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-primary"
                  onClick={() =>
                    setHistoryExercise({
                      id: exercise.exerciseId,
                      name: exercise.exerciseName || "Hareket",
                    })
                  }
                >
                  <History className="h-4 w-4" />
                </Button>
                <span className="hidden text-muted-foreground text-xs md:inline">
                  {exercise.sets}×{exercise.reps}
                  {exercise.percentageOf1RM && ` @${exercise.percentageOf1RM}%`}
                </span>
              </div>
              <div className="flex items-center gap-3 pl-8 font-semibold text-[10px] text-muted-foreground uppercase tracking-wider">
                <span className="flex items-center gap-1">
                  <Timer className="h-3 w-3" />
                  {formatRest(exercise.restSeconds)} Dinlenme
                </span>
                {exercise.notes?.trim() && exercise.notes !== "null" && (
                  <div className="flex min-w-0 max-w-[80px] items-center gap-1 text-primary sm:max-w-[80px] lg:max-w-[80px]">
                    <MessageSquare className="h-3 w-3 shrink-0" />
                    <span className="truncate">Not: {exercise.notes}</span>
                  </div>
                )}
                {
                  (userRole === "admin" || isCoachingClient) &&
                    data.exerciseData.setLogs.length === 1 &&
                    null /* Single set video button removed, now placed in notes area */
                }
              </div>
            </div>
          );
        },
      },

      {
        id: "targetWeight",
        header: () => (
          <div className="text-center">
            Hedef <span className="hidden md:inline">Kg</span>
          </div>
        ),
        cell: ({ row }: { row: { original: FlattenedRow } }) => {
          const data = row.original;
          const targetWeight =
            data.exerciseData.exercise.calculatedTargetWeight;
          const isEstimated =
            data.exerciseData.exercise.isTargetWeightEstimated;

          if (targetWeight === null || targetWeight === undefined) {
            return <div className="text-center text-muted-foreground">-</div>;
          }

          return (
            <div className="flex items-center justify-center gap-1 font-medium tabular-nums">
              <span>{targetWeight}kg</span>
              {isEstimated && (
                <span
                  className="text-[10px] text-amber-500"
                  title="RPE verilerinden tahmin edildi"
                >
                  ~
                </span>
              )}
            </div>
          );
        },
      },

      // Completed Weight Column
      {
        id: "completedWeight",
        header: () => (
          <div className="text-center">
            Yapılan <span className="hidden md:inline">Kg</span>
          </div>
        ),
        cell: ({ row }: { row: { original: FlattenedRow } }) => {
          const data = row.original;

          // For exercise rows with single set
          if (
            data.type === "exercise" &&
            data.exerciseData.setLogs.length === 1
          ) {
            const setData = data.exerciseData.setLogs[0];
            return (
              <div className="flex justify-center">
                <WeightInput
                  value={setData.completedWeight}
                  targetWeight={
                    data.exerciseData.exercise.calculatedTargetWeight
                  }
                  onChange={(value) =>
                    handleUpdateRef.current(
                      data.exerciseData.exercise.exerciseId,
                      data.exerciseData.exercise.order,
                      1,
                      { completedWeight: value },
                    )
                  }
                  showButtons={false}
                  className="w-[85px]"
                  disabled={disabled}
                />
              </div>
            );
          }

          // For exercise rows with multiple sets - show summary
          if (data.type === "exercise") {
            const completed = data.exerciseData.setLogs.filter(
              (s: SetLogData) => s.completedWeight !== null,
            ).length;
            const total = data.exerciseData.setLogs.length;
            return (
              <div className="text-center text-muted-foreground text-sm">
                {completed}/{total} set
              </div>
            );
          }

          // For set rows
          if (data.type === "set" && data.setData) {
            return (
              <div className="flex justify-center">
                <WeightInput
                  value={data.setData.completedWeight}
                  targetWeight={
                    data.exerciseData.exercise.calculatedTargetWeight
                  }
                  onChange={(value) =>
                    handleUpdateRef.current(
                      data.exerciseData.exercise.exerciseId,
                      data.exerciseData.exercise.order,
                      data.setData!.setNumber,
                      { completedWeight: value },
                    )
                  }
                  showButtons={false}
                  className="w-[85px]"
                  disabled={disabled}
                  data-testid={`weight-input-${data.exerciseData.exercise.exerciseId}-${data.exerciseData.exercise.order}-set${data.setData.setNumber}`}
                />
              </div>
            );
          }

          return null;
        },
      },

      // Completed Reps Column
      {
        id: "completedReps",
        header: () => (
          <div className="text-center">
            Yapılan <span className="hidden md:inline">Tekrar</span>
          </div>
        ),
        cell: ({ row }: { row: { original: FlattenedRow } }) => {
          const data = row.original;

          // For exercise rows with single set
          if (
            data.type === "exercise" &&
            data.exerciseData.setLogs.length === 1
          ) {
            const setData = data.exerciseData.setLogs[0];
            return (
              <div className="flex justify-center">
                <RepsInput
                  value={setData.completedReps}
                  placeholder={extractDefaultReps(
                    data.exerciseData.exercise.reps,
                  )}
                  onChange={(val) =>
                    handleUpdateRef.current(
                      data.exerciseData.exercise.exerciseId,
                      data.exerciseData.exercise.order,
                      1,
                      { completedReps: val },
                    )
                  }
                  className="h-9 w-20 text-center text-sm"
                  disabled={disabled}
                />
              </div>
            );
          }

          // For exercise rows with multiple sets
          if (data.type === "exercise") {
            return null;
          }

          // For set rows
          if (data.type === "set" && data.setData) {
            return (
              <div className="flex justify-center">
                <RepsInput
                  value={data.setData.completedReps}
                  placeholder={extractDefaultReps(
                    data.exerciseData.exercise.reps,
                  )}
                  onChange={(val) =>
                    handleUpdateRef.current(
                      data.exerciseData.exercise.exerciseId,
                      data.exerciseData.exercise.order,
                      data.setData!.setNumber,
                      { completedReps: val },
                    )
                  }
                  className="h-9 w-20 text-center text-sm"
                  disabled={disabled}
                  data-testid={`reps-input-${data.exerciseData.exercise.exerciseId}-${data.exerciseData.exercise.order}-set${data.setData.setNumber}`}
                />
              </div>
            );
          }

          return null;
        },
      },

      // Target RPE Column (hidden on mobile)
      {
        id: "targetRPE",
        header: () => (
          <div className="hidden text-center md:block">Hedef RPE</div>
        ),
        cell: ({ row }: { row: { original: FlattenedRow } }) => {
          const data = row.original;
          const targetRPE = data.exerciseData.exercise.targetRPE;

          return (
            <div className="hidden text-center md:block">
              <RPEBadge value={targetRPE} />
            </div>
          );
        },
      },

      // Completed RPE Column (hidden on mobile)
      {
        id: "completedRPE",
        header: () => <div className="text-center">Yapılan RPE</div>,
        cell: ({ row }: { row: { original: FlattenedRow } }) => {
          const data = row.original;

          // For exercise rows with single set
          if (
            data.type === "exercise" &&
            data.exerciseData.setLogs.length === 1
          ) {
            const setData = data.exerciseData.setLogs[0];
            return (
              <div className="flex justify-center">
                <RPEInput
                  value={setData.completedRPE}
                  targetRPE={data.exerciseData.exercise.targetRPE}
                  onChange={(value) =>
                    handleUpdateRef.current(
                      data.exerciseData.exercise.exerciseId,
                      data.exerciseData.exercise.order,
                      1,
                      { completedRPE: value },
                    )
                  }
                  className="w-20"
                  disabled={disabled}
                />
              </div>
            );
          }

          // For set rows
          if (data.type === "set" && data.setData) {
            return (
              <div className="flex justify-center">
                <RPEInput
                  value={data.setData!.completedRPE}
                  targetRPE={data.exerciseData.exercise.targetRPE}
                  onChange={(value) =>
                    handleUpdateRef.current(
                      data.exerciseData.exercise.exerciseId,
                      data.exerciseData.exercise.order,
                      data.setData!.setNumber,
                      { completedRPE: value },
                    )
                  }
                  className="w-20"
                  disabled={disabled}
                  data-testid={`rpe-input-${data.exerciseData.exercise.exerciseId}-${data.exerciseData.exercise.order}-set${data.setData.setNumber}`}
                />
              </div>
            );
          }

          return null;
        },
      },
    ];
  }, [
    expanded,
    handleExerciseClick,
    disabled,
    formatRest,
    extractDefaultReps,
    userRole,
    isCoachingClient,
  ]);

  // Filter visible rows based on expansion state
  const visibleData = useMemo(() => {
    return (tableData as FlattenedRow[]).filter((row: FlattenedRow) => {
      if (row.type === "exercise") return true;
      // Show set rows only if parent exercise is expanded
      const parentId = row.id.replace(/-set-\d+$/, "");
      return expanded[parentId] ?? false;
    });
  }, [tableData, expanded]);

  const table = useReactTable({
    data: visibleData,
    columns,
    state: { expanded: expanded as ExpandedState },
    onExpandedChange: (updater) => {
      if (typeof updater === "function") {
        setExpanded((prev) => updater(prev) as Record<string, boolean>);
      } else {
        setExpanded(updater as Record<string, boolean>);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <>
      <div
        className={cn(
          "hidden w-full overflow-x-auto rounded-lg border md:block",
          className,
        )}
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Yükleniyor...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Henüz hareket eklenmemiş.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.flatMap((row) => {
                const data = row.original;
                const setLog =
                  data.type === "exercise" &&
                  data.exerciseData.setLogs.length === 1
                    ? data.exerciseData.setLogs[0]
                    : data.setData;

                const mainRow = (
                  <TableRow
                    key={row.id}
                    className={cn(
                      "transition-colors hover:bg-muted/10",
                      data.type === "set" && "bg-muted/30",
                      data.type === "exercise" && "bg-secondary/10 font-medium",
                      setLog ? "border-b-0" : "border-b",
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-2">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );

                // Notes sub-row (if applicable)
                const notesRow = setLog
                  ? (() => {
                      const isCoaching =
                        userRole === "admin" || isCoachingClient;
                      const processingStatus = setLog.videoProcessingStatus;
                      const rowKey = `${data.exerciseData.exercise.exerciseId}-${data.exerciseData.exercise.order}-${setLog.setNumber}`;
                      const isUploading = uploadingItems.some(
                        (item) =>
                          item.rowId === rowKey && item.status === "uploading",
                      );
                      const isProcessing =
                        processingStatus === "pending" ||
                        processingStatus === "processing" ||
                        isUploading;
                      const hasReadyVideo =
                        !!setLog.videoUrl &&
                        (!processingStatus || processingStatus === "done");

                      return (
                        <TableRow
                          key={`${row.id}-notes`}
                          className={cn(
                            "border-b transition-colors hover:bg-muted/10",
                            data.type === "set" && "bg-muted/30",
                            data.type === "exercise" && "bg-secondary/10",
                          )}
                        >
                          <TableCell
                            colSpan={columns.length}
                            className="pt-0 pb-2 pl-12"
                          >
                            <div className="flex items-start gap-2 rounded-md border border-border/20 bg-background/50 p-2">
                              <MessageSquare className="mt-1 h-3 w-3 shrink-0 text-muted-foreground" />
                              <div className="flex flex-1 items-center gap-2">
                                <div className="flex-1">
                                  <NoteInput
                                    initialValue={setLog.notes || ""}
                                    disabled={disabled}
                                    onSave={(val) => {
                                      handleUpdateRef.current(
                                        data.exerciseData.exercise.exerciseId,
                                        data.exerciseData.exercise.order,
                                        setLog.setNumber,
                                        { notes: val },
                                      );
                                    }}
                                  />
                                </div>
                                {isCoaching && (
                                  <Button
                                    variant={
                                      hasReadyVideo ? "secondary" : "outline"
                                    }
                                    size="icon"
                                    disabled={isProcessing}
                                    title={
                                      isProcessing
                                        ? "Video işleniyor..."
                                        : "Video Ekle / İzle"
                                    }
                                    className={cn(
                                      "h-7 w-7 shrink-0 rounded-md transition-all",
                                      hasReadyVideo
                                        ? "bg-primary text-primary-foreground"
                                        : isProcessing
                                          ? "cursor-not-allowed border-amber-400/50 border-dashed bg-amber-400/10 text-amber-500"
                                          : "border-muted-foreground/30 border-dashed bg-muted/10 text-muted-foreground/50 hover:border-primary/50 hover:bg-primary/5 hover:text-primary",
                                    )}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (hasReadyVideo && setLog.videoUrl) {
                                        setViewingVideoSet({
                                          logId: setLog.logId!,
                                          exerciseId:
                                            data.exerciseData.exercise
                                              .exerciseId,
                                          exerciseOrder:
                                            data.exerciseData.exercise.order,
                                          setNumber: setLog.setNumber,
                                          videoUrl: setLog.videoUrl,
                                          thumbnailUrl:
                                            setLog.videoThumbnailUrl ?? null,
                                          reps: setLog.completedReps,
                                          weekNumber: weekNumber,
                                        });
                                      } else if (!isProcessing) {
                                        setActiveSetForVideo({
                                          logId: setLog.logId,
                                          exerciseId:
                                            data.exerciseData.exercise
                                              .exerciseId,
                                          exerciseOrder:
                                            data.exerciseData.exercise.order,
                                          setNumber: setLog.setNumber,
                                          reps: setLog.completedReps,
                                          existingVideoUrl:
                                            setLog.videoUrl ?? null,
                                          existingThumbnailUrl:
                                            setLog.videoThumbnailUrl ?? null,
                                        });
                                        setVideoTrimmerOpen(true);
                                      }
                                    }}
                                  >
                                    {hasReadyVideo ? (
                                      <Play className="h-4 w-4 fill-current" />
                                    ) : isProcessing ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <VideoIcon className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })()
                  : null;

                return notesRow ? [mainRow, notesRow] : [mainRow];
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Accordion Card View */}
      <div className={cn("md:hidden", className)}>
        <Accordion
          type="multiple"
          value={Object.keys(expanded).filter((k) => expanded[k])}
          onValueChange={(value) => {
            const newExpanded: Record<string, boolean> = {};
            for (const id of value) {
              newExpanded[id] = true;
            }
            setExpanded(newExpanded);
          }}
          className="flex flex-col gap-4"
        >
          {exercises.map((exerciseData) => {
            const exercise = exerciseData.exercise;
            const exerciseRowId = `${exercise.exerciseId}-${exercise.order}`;
            const completedSets = exerciseData.setLogs.filter(
              (s) => s.completedWeight !== null,
            ).length;
            const totalSets = exerciseData.setLogs.length;

            return (
              <AccordionItem
                key={exerciseRowId}
                value={exerciseRowId}
                className="overflow-hidden rounded-lg border bg-card"
              >
                <AccordionTrigger
                  className={cn(
                    "bg-card px-3 py-3 hover:bg-muted/10 hover:no-underline data-[state=open]:bg-secondary/20",
                  )}
                >
                  <div className="flex min-w-0 flex-1 items-center justify-between pr-2">
                    <div className="flex min-w-0 flex-col items-start text-left">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExerciseClick(exerciseData);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            handleExerciseClick(exerciseData);
                          }
                        }}
                        className="flex min-w-0 max-w-[55vw] cursor-pointer items-center gap-2 font-medium text-primary text-sm active:text-primary/80 sm:max-w-[70vw]"
                      >
                        <span className="truncate border-primary/30 border-b border-dashed">
                          {exercise.exerciseName || "Hareket"}
                        </span>
                        <Info className="h-3 w-3 shrink-0 text-muted-foreground" />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-normal text-[10px] text-muted-foreground uppercase tracking-wider">
                          {exercise.sets}×{exercise.reps}{" "}
                          {exercise.percentageOf1RM &&
                            `@${exercise.percentageOf1RM}%`}
                        </span>
                        <span className="flex items-center gap-1 font-normal text-[10px] text-secondary-foreground uppercase tracking-wider">
                          <Timer className="h-3 w-3" />
                          {formatRest(exercise.restSeconds)}
                        </span>
                        {exercise.notes?.trim() &&
                          exercise.notes !== "null" && (
                            <span className="flex items-center gap-1 font-normal text-[10px] text-primary uppercase tracking-wider">
                              <MessageSquare className="h-3 w-3" />
                              Not Var
                            </span>
                          )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div
                        role="button"
                        tabIndex={0}
                        className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md font-medium text-muted-foreground text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          setHistoryExercise({
                            id: exercise.exerciseId,
                            name: exercise.exerciseName || "Hareket",
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            setHistoryExercise({
                              id: exercise.exerciseId,
                              name: exercise.exerciseName || "Hareket",
                            });
                          }
                        }}
                      >
                        <History className="h-4 w-4" />
                      </div>
                      <div className="mr-1 text-right text-xs">
                        <div
                          className={cn(
                            "font-medium",
                            completedSets === totalSets ? "text-primary" : "",
                          )}
                        >
                          {completedSets}/{totalSets}
                        </div>
                        <div className="font-normal text-[10px] opacity-70">
                          Set
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="divide-y border-t bg-card/50 px-0 pb-0">
                  {exerciseData.setLogs.map((set, index) => (
                    <div key={index} className="p-3">
                      <div className="grid grid-cols-[auto_1.2fr_0.8fr_1fr] items-center gap-3">
                        <div className="flex flex-col items-center gap-1">
                          <span className="flex h-6 w-6 items-center justify-center rounded bg-muted/30 font-medium font-mono text-muted-foreground text-xs">
                            #{set.setNumber}
                          </span>
                          {
                            (userRole === "admin" || isCoachingClient) &&
                              null /* Mobile set video button removed, now placed in notes area */
                          }
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className="text-center font-medium text-[10px] text-muted-foreground">
                            Hedef:{" "}
                            {set.targetWeight ??
                              exercise.calculatedTargetWeight ??
                              "-"}{" "}
                            kg
                            {exercise.isTargetWeightEstimated && (
                              <span className="ml-0.5 text-primary opacity-80">
                                ~
                              </span>
                            )}
                          </span>
                          <WeightInput
                            value={set.completedWeight}
                            targetWeight={
                              set.targetWeight ??
                              exercise.calculatedTargetWeight
                            }
                            onChange={(val) =>
                              handleUpdateRef.current(
                                exercise.exerciseId,
                                exercise.order,
                                set.setNumber,
                                { completedWeight: val },
                              )
                            }
                            showButtons={false}
                            className="h-10 w-full text-base"
                            disabled={disabled}
                          />
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className="text-center font-medium text-[10px] text-muted-foreground">
                            Tekrar
                          </span>
                          <RepsInput
                            value={set.completedReps}
                            placeholder={extractDefaultReps(exercise.reps)}
                            onChange={(val) =>
                              handleUpdateRef.current(
                                exercise.exerciseId,
                                exercise.order,
                                set.setNumber,
                                { completedReps: val },
                              )
                            }
                            className="h-10 w-full text-center text-base"
                            disabled={disabled}
                          />
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className="text-center font-medium text-[10px] text-muted-foreground">
                            RPE{" "}
                            {(set.targetRPE ?? exercise.targetRPE)
                              ? `(${set.targetRPE ?? exercise.targetRPE})`
                              : ""}
                          </span>
                          <RPEInput
                            value={set.completedRPE}
                            targetRPE={set.targetRPE ?? exercise.targetRPE}
                            onChange={(val) =>
                              handleUpdateRef.current(
                                exercise.exerciseId,
                                exercise.order,
                                set.setNumber,
                                { completedRPE: val },
                              )
                            }
                            compact
                            className="w-full"
                            disabled={disabled}
                          />
                        </div>
                      </div>

                      {/* Set-specific Notes for Mobile */}
                      <div className="mt-3 flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="flex flex-1 items-center gap-2">
                          <div className="flex-1">
                            <NoteInput
                              initialValue={set.notes || ""}
                              disabled={disabled}
                              onSave={(val) => {
                                handleUpdateRef.current(
                                  exercise.exerciseId,
                                  exercise.order,
                                  set.setNumber,
                                  { notes: val },
                                );
                              }}
                            />
                          </div>
                          {(userRole === "admin" || isCoachingClient) &&
                            (() => {
                              const mobileStatus = set.videoProcessingStatus;
                              const rowKey = `${exercise.exerciseId}-${exercise.order}-${set.setNumber}`;
                              const isUploading = uploadingItems.some(
                                (item) =>
                                  item.rowId === rowKey &&
                                  item.status === "uploading",
                              );
                              const mobileProcessing =
                                mobileStatus === "pending" ||
                                mobileStatus === "processing" ||
                                isUploading;
                              const mobileReady =
                                !!set.videoUrl &&
                                (!mobileStatus || mobileStatus === "done");
                              return (
                                <Button
                                  variant={
                                    mobileReady ? "secondary" : "outline"
                                  }
                                  size="icon"
                                  disabled={mobileProcessing}
                                  title={
                                    mobileProcessing
                                      ? "Video işleniyor..."
                                      : "Video Ekle / İzle"
                                  }
                                  className={cn(
                                    "h-7 w-7 shrink-0 rounded-md transition-all",
                                    mobileReady
                                      ? "bg-secondary text-primary hover:bg-secondary/80"
                                      : mobileProcessing
                                        ? "cursor-not-allowed border-amber-400/50 border-dashed bg-amber-400/10 text-amber-500"
                                        : "border-muted-foreground/30 border-dashed bg-muted/10 text-muted-foreground/50 hover:border-primary/50 hover:bg-primary/5 hover:text-primary",
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (mobileReady) {
                                      setViewingVideoSet({
                                        logId: set.logId!,
                                        exerciseId: exercise.exerciseId,
                                        exerciseOrder: exercise.order,
                                        setNumber: set.setNumber,
                                        videoUrl: set.videoUrl!,
                                        thumbnailUrl:
                                          set.videoThumbnailUrl ?? null,
                                        reps: set.completedReps,
                                        weekNumber: weekNumber,
                                      });
                                    } else if (!mobileProcessing) {
                                      setActiveSetForVideo({
                                        logId: set.logId,
                                        exerciseId: exercise.exerciseId,
                                        exerciseOrder: exercise.order,
                                        setNumber: set.setNumber,
                                        reps: set.completedReps,
                                        existingVideoUrl: set.videoUrl ?? null,
                                        existingThumbnailUrl:
                                          set.videoThumbnailUrl ?? null,
                                      });
                                      setVideoTrimmerOpen(true);
                                    }
                                  }}
                                >
                                  {mobileReady ? (
                                    <VideoIcon
                                      className="h-4 w-4"
                                      strokeWidth={2}
                                    />
                                  ) : mobileProcessing ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <VideoIcon className="h-4 w-4" />
                                  )}
                                </Button>
                              );
                            })()}
                        </div>
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      {/* Exercise Detail Dialog */}
      <ExerciseDetailDialog
        exercise={
          selectedExercise
            ? {
                exerciseId: selectedExercise.exercise.exerciseId,
                name: selectedExercise.exercise.exerciseName || "Hareket",
                muscleGroup:
                  (selectedExercise.exerciseInfo?.muscleGroup as string) ??
                  undefined,
                videoUrl: selectedExercise.exerciseInfo?.videoUrl ?? undefined,
                descriptionUrl:
                  selectedExercise.exerciseInfo?.descriptionUrl ?? undefined,
                instructions:
                  selectedExercise.exerciseInfo?.instructions ?? undefined,
                notes: selectedExercise.exercise.notes,
              }
            : null
        }
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
      />

      {/* History Drawer */}
      <WorkoutHistoryDrawer
        exerciseId={historyExercise?.id || ""}
        exerciseName={historyExercise?.name || ""}
        open={!!historyExercise}
        onOpenChange={(open) => !open && setHistoryExercise(null)}
      />

      <VideoTrimmerDialog
        open={videoTrimmerOpen}
        onOpenChange={(open) => {
          setVideoTrimmerOpen(open);
          if (!open) {
            setActiveSetForVideo(null);
          }
        }}
        onSuccess={handleVideoUpload}
        rowId={
          activeSetForVideo
            ? `${activeSetForVideo.exerciseId}-${activeSetForVideo.exerciseOrder}-${activeSetForVideo.setNumber}`
            : undefined
        }
        exerciseId={activeSetForVideo?.exerciseId}
        exerciseOrder={activeSetForVideo?.exerciseOrder}
        setNumber={activeSetForVideo?.setNumber}
      />

      <Dialog
        open={!!viewingVideoSet}
        onOpenChange={(open) => !open && setViewingVideoSet(null)}
      >
        <DialogContent className="flex max-h-[95vh] w-[95vw] max-w-4xl flex-col overflow-hidden rounded-xl border bg-background p-0 sm:w-full">
          <DialogHeader className="border-b px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-1 text-left sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="min-w-0 flex-1">
                <h3 className="truncate font-bold text-foreground text-lg sm:text-xl">
                  {exercises.find(
                    (e) =>
                      e.exercise.exerciseId === viewingVideoSet?.exerciseId,
                  )?.exercise.exerciseName || "Egzersiz"}
                </h3>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-semibold text-[10px] text-muted-foreground uppercase tracking-wide sm:text-xs">
                  {viewingVideoSet?.weekNumber && (
                    <span className="flex items-center gap-1">
                      <span className="opacity-50">Hafta</span>
                      <span className="text-primary">
                        {viewingVideoSet.weekNumber}
                      </span>
                    </span>
                  )}
                  <span className="h-1 w-1 rounded-full bg-border" />
                  <span className="flex items-center gap-1">
                    <span className="opacity-50">Set</span>
                    <span className="text-primary">
                      {viewingVideoSet?.setNumber}
                    </span>
                  </span>
                  {viewingVideoSet?.reps && (
                    <>
                      <span className="h-1 w-1 rounded-full bg-border" />
                      <span className="flex items-center gap-1">
                        <span className="text-primary">
                          {viewingVideoSet.reps}
                        </span>
                        <span className="opacity-50">Tekrar</span>
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </DialogHeader>

          {viewingVideoSet && (
            <>
              {/* Video Player */}
              <div className="relative flex min-h-[30vh] w-full flex-1 items-center justify-center bg-black/95">
                <video
                  src={viewingVideoSet.videoUrl}
                  controls
                  autoPlay
                  playsInline
                  className="max-h-[60vh] w-full object-contain sm:max-h-[70vh]"
                  onError={() => {
                    // NotSupportedError / no supported sources — browser shows native error UI
                    // handled here to prevent unhandled rejection in Sentry
                  }}
                />
              </div>

              {/* Footer Actions Bar */}
              <div className="flex flex-col items-center justify-between gap-3 border-t bg-muted/20 p-4 sm:flex-row sm:px-6">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={deleteVideoMutation.isPending}
                  className="h-9 w-full rounded-lg text-destructive hover:bg-destructive/10 hover:text-destructive sm:w-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    const confirmDelete = window.confirm(
                      "Bu videoyu silmek istediğinize emin misiniz?",
                    );
                    if (confirmDelete) {
                      const toDelete = viewingVideoSet;
                      setViewingVideoSet(null);

                      // 1. S3 + object tablosundan temizle
                      deleteVideoMutation
                        .mutateAsync({
                          videoKey: toDelete.videoUrl,
                          thumbnailKey: toDelete.thumbnailUrl ?? undefined,
                        })
                        .catch(() => {
                          /* ignore */
                        });

                      // 2. Log'daki video alanlarını null yap
                      if (onLogUpdateAsync) {
                        onLogUpdateAsync(
                          toDelete.exerciseId,
                          toDelete.exerciseOrder,
                          toDelete.setNumber,
                          {
                            videoUrl: null,
                            videoProcessingStatus: null,
                            videoTrimStart: null,
                            videoTrimEnd: null,
                          },
                        ).catch((err) =>
                          console.error("Video log güncellenirken hata:", err),
                        );
                      }
                    }
                  }}
                >
                  {deleteVideoMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  Videoyu Sil
                </Button>

                <div className="flex w-full items-center gap-2 sm:w-auto">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-9 flex-1 rounded-lg font-bold text-xs sm:flex-none sm:px-4"
                    onClick={(e) => {
                      e.stopPropagation();
                      const setInfo = viewingVideoSet;
                      setViewingVideoSet(null);
                      setActiveSetForVideo({
                        exerciseId: setInfo.exerciseId,
                        exerciseOrder: setInfo.exerciseOrder,
                        setNumber: setInfo.setNumber,
                        reps: setInfo.reps,
                        existingVideoUrl: setInfo.videoUrl,
                        existingThumbnailUrl: setInfo.thumbnailUrl ?? null,
                      });
                      setVideoTrimmerOpen(true);
                    }}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Yeni Video / Kırp
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
